import { z } from "zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
} from "@/components/ui/form";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import { Button } from "@/components/ui/button";
import {
  useAddEducationMutation,
  useUpdateEducationMutation,
} from "@/hooks/api/education.queries";
import { useProfileQuery } from "@/hooks/api/profile.queries";
import { EducationType } from "@/services/education.service";
import { useEffect } from "react";
import { GraduationCap, Save } from "lucide-react";

const educationSchema = z.object({
  college_name: z.string().min(2, "College name is required"),
  degree_name: z.string().min(2, "Degree name is required"),
  degree_type: z.enum(["ug", "pg", "phd", "diploma", "certificate"]),
  roll_number: z.string().min(1, "Roll number is required"),
  start_year: z.coerce.number().min(1900).max(new Date().getFullYear() + 5),
  end_year: z.coerce.number().min(1900).max(new Date().getFullYear() + 10),
  status: z.enum(["active", "completed", "on_hold", "dropped"]),
});

type EducationFormValues = z.infer<typeof educationSchema>;

interface EducationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  initialData?: EducationType | null;
}

const EducationDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
  initialData,
}: EducationDialogProps) => {
  const isEditing = !!initialData;
  const { data: profile } = useProfileQuery();
  const addMutation = useAddEducationMutation();
  const updateMutation = useUpdateEducationMutation();

  const form = useForm<EducationFormValues>({
    resolver: zodResolver(educationSchema) as any,
    defaultValues: {
      college_name: "",
      degree_name: "",
      degree_type: "ug",
      roll_number: "",
      start_year: new Date().getFullYear(),
      end_year: new Date().getFullYear() + 4,
      status: "active",
    },
  });

  useEffect(() => {
    if (initialData && isOpen) {
      form.reset({
        college_name: initialData.college_name ?? "",
        degree_name: initialData.degree_name ?? "",
        degree_type: (initialData.degree_type as any) ?? "ug",
        roll_number: initialData.roll_number ?? "",
        start_year: initialData.start_year ?? new Date().getFullYear(),
        end_year: initialData.end_year ?? new Date().getFullYear() + 4,
        status: (initialData.status as any) ?? "active",
      });
    } else if (!isOpen) {
      form.reset({
        college_name: "",
        degree_name: "",
        degree_type: "ug",
        roll_number: "",
        start_year: new Date().getFullYear(),
        end_year: new Date().getFullYear() + 4,
        status: "active",
      });
    }
  }, [initialData, isOpen, form]);

  const onSubmit: SubmitHandler<EducationFormValues> = (values) => {
    // Include college_id from profile if available
    const payload = {
      ...values,
      college_id: profile?.college_id,
    };

    if (isEditing && initialData) {
      updateMutation.mutate(
        { id: initialData.id, data: payload },
        {
          onSuccess: () => {
            onCancel();
          },
        }
      );
    } else {
      addMutation.mutate(payload, {
        onSuccess: () => {
          onCancel();
        },
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-[2rem] border-none shadow-2xl p-0 overflow-hidden bg-white">
        <DialogHeader className="bg-indigo-600 p-8 text-white">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <GraduationCap size={24} className="text-white" />
             </div>
             <div>
                <DialogTitle className="text-2xl font-black">{isEditing ? "Edit Education" : "Add Education"}</DialogTitle>
                <DialogDescription className="text-indigo-100">
                  {isEditing ? "Update your academic details." : "Share your educational journey."}
                </DialogDescription>
             </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-8 space-y-6">
            <InputField
              type="text"
              name="college_name"
              label="College / Institution"
              placeholder="e.g. Stanford University"
              className={{
                labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600"
              }}
            />

            <div className="grid grid-cols-2 gap-4">
              <InputField
                type="text"
                name="degree_name"
                label="Degree Name"
                placeholder="e.g. B.Tech CS"
                className={{
                  labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                  inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600"
                }}
              />
              <SelectField
                name="degree_type"
                label="Degree Type"
                placeholder="Select type"
                options={[
                  { label: "Bachelors (UG)", value: "ug" },
                  { label: "Masters (PG)", value: "pg" },
                  { label: "PhD", value: "phd" },
                  { label: "Diploma", value: "diploma" },
                  { label: "Certificate", value: "certificate" },
                ]}
                className={{
                  labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                  selectClass: "h-12 rounded-xl bg-slate-50 border-none focus:ring-indigo-600"
                }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
               <InputField
                type="number"
                name="start_year"
                label="Start Year"
                className={{
                  labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                  inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600"
                }}
              />
              <InputField
                type="number"
                name="end_year"
                label="End Year"
                className={{
                  labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                  inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600"
                }}
              />
              <SelectField
                name="status"
                label="Status"
                placeholder="Select status"
                options={[
                  { label: "Active", value: "active" },
                  { label: "Completed", value: "completed" },
                  { label: "On Hold", value: "on_hold" },
                  { label: "Dropped", value: "dropped" },
                ]}
                className={{
                  labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                  selectClass: "h-12 rounded-xl bg-slate-50 border-none focus:ring-indigo-600"
                }}
              />
            </div>

            <InputField
              type="text"
              name="roll_number"
              label="Roll/Reg Number"
              placeholder="e.g. 2021CS001"
              className={{
                labelClass: "text-xs font-bold uppercase tracking-widest text-slate-400",
                inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600"
              }}
            />

            <DialogFooter className="pt-4 flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600 font-bold"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 font-bold shadow-lg shadow-indigo-100"
                disabled={addMutation.isPending || updateMutation.isPending}
              >
                {addMutation.isPending || updateMutation.isPending ? "Saving..." : <><Save size={18} className="mr-2" /> {isEditing ? "Update" : "Add Record"}</>}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EducationDialog;
